"use client"

import { useState } from "react"
import Link from "next/link"
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  FileText,
  GraduationCap,
  Lock,
  MapPin,
  ShieldCheck,
  Smartphone,
  UserRound,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/utils/utils"
import { LINKS } from "./links"

type Audience = "aluno" | "cfc" | "autonomo"
type Tone = "green" | "amber" | "blue"

const toneChip: Record<Tone, string> = {
  green: "bg-primary/10 text-primary",
  amber: "bg-amber-500/10 text-amber-600",
  blue: "bg-sky-600/10 text-sky-700",
}

type Row = {
  icon: LucideIcon
  label: string
  meta: string
  tag: string
  tone: Tone
}

type Persona = {
  tag: string
  h1: string
  sub: string
  ctaLabel: string
  ctaHref: string
  cta2Label: string
  cta2Href: string
  proof1: string
  proof2: string
  winlabel: string
  kicker: string
  title: string
  screenCta: string
  rows: Row[]
}

const PERSONAS: Record<Audience, Persona> = {
  aluno: {
    tag: "Para você, aluno",
    h1: "Encontre sua autoescola e agende aulas pelo celular",
    sub: "O CanalCNH conecta você ao CFC ou instrutor da sua região. Agende, acompanhe cada aula e baixe seu contrato — tudo pelo app Agenda CFC.",
    ctaLabel: "Baixar o app grátis",
    ctaHref: LINKS.appAndroid,
    cta2Label: "Como funciona",
    cta2Href: "#alunos",
    proof1: "Grátis para o aluno",
    proof2: "Encontre por localização",
    winlabel: "Agenda CFC · app do aluno",
    kicker: "Perto de você",
    title: "Escolha seu CFC",
    screenCta: "Conectar à minha conta",
    rows: [
      { icon: MapPin, label: "Auto Escola Bom Motorista", meta: "Centro · 1,2 km", tag: "Aberto", tone: "green" },
      { icon: MapPin, label: "CFC Direção Segura", meta: "Jardim América · 2,4 km", tag: "Aberto", tone: "green" },
      { icon: Users, label: "Carlos — Instrutor autônomo", meta: "Categoria B · 3,0 km", tag: "Novo", tone: "blue" },
    ],
  },
  cfc: {
    tag: "Para o seu CFC",
    h1: "Gerencie todo o seu CFC em um só painel",
    sub: "Agenda, alunos, instrutores, contratos e financeiro no mesmo lugar — com o ambiente da sua escola totalmente isolado. Menos planilha, mais aulas.",
    ctaLabel: "Testar 14 dias grátis",
    ctaHref: LINKS.register,
    cta2Label: "Ver planos",
    cta2Href: "#planos",
    proof1: "Sem cartão para testar",
    proof2: "Cancele quando quiser",
    winlabel: "painel.canalcnh.com.br",
    kicker: "Hoje · terça",
    title: "Agenda do dia",
    screenCta: "Abrir agenda completa",
    rows: [
      { icon: CalendarDays, label: "08:00 · João Pedro", meta: "Instrutor Carlos · Categoria B", tag: "Confirmada", tone: "green" },
      { icon: CalendarDays, label: "09:30 · Marina Alves", meta: "Instrutora Ana · Categoria A", tag: "Aguardando", tone: "amber" },
      { icon: Wallet, label: "Mensalidade gerada", meta: "12 alunos · vence dia 10", tag: "R$ 3,4k", tone: "green" },
    ],
  },
  autonomo: {
    tag: "Para o instrutor autônomo",
    h1: "Seja seu próprio CFC, sem sistema caro",
    sub: "Cadastre-se como seu próprio ambiente e use o mesmo poder de uma autoescola — no seu nome. Sua agenda, seus alunos, suas cobranças e contratos.",
    ctaLabel: "Começar como autônomo",
    ctaHref: LINKS.register,
    cta2Label: "Ver planos",
    cta2Href: "#planos",
    proof1: "Tudo no seu nome",
    proof2: "Comece em minutos",
    winlabel: "CanalCNH · instrutor",
    kicker: "Sua semana",
    title: "Meus alunos",
    screenCta: "Definir disponibilidade",
    rows: [
      { icon: CalendarDays, label: "Seg 14:00 · Rafael", meta: "Aula prática · Categoria B", tag: "Confirmada", tone: "green" },
      { icon: Wallet, label: "Pacote de 10 aulas", meta: "Beatriz · 6 de 10 usadas", tag: "Pago", tone: "green" },
      { icon: FileText, label: "Contrato enviado", meta: "Beatriz · por e-mail", tag: "PDF", tone: "blue" },
    ],
  },
}

const TABS: { key: Audience; label: string; icon: LucideIcon }[] = [
  { key: "aluno", label: "Aluno", icon: GraduationCap },
  { key: "cfc", label: "Sou CFC", icon: Building2 },
  { key: "autonomo", label: "Instrutor autônomo", icon: UserRound },
]

const PROOF_STRIP = [
  { icon: ShieldCheck, label: "Dados isolados por escola" },
  { icon: Lock, label: "Conforme a LGPD" },
  { icon: CalendarDays, label: "14 dias grátis, sem cartão" },
  { icon: Smartphone, label: "App para aluno e instrutor" },
]

export function Hero() {
  const [audience, setAudience] = useState<Audience>("aluno")
  const p = PERSONAS[audience]

  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1100px 520px at 82% -8%, color-mix(in oklch, var(--primary), transparent 82%), transparent 60%)," +
          "radial-gradient(760px 420px at 8% 0%, color-mix(in oklch, var(--primary), transparent 88%), transparent 55%)," +
          "var(--background)",
      }}
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-6 pt-16 pb-20 lg:grid-cols-[1.08fr_.92fr] lg:px-10">
        {/* left */}
        <div className="animate-in fade-in slide-in-from-bottom-3 duration-700">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-[13px] font-semibold text-primary">
            {p.tag}
          </span>
          <h1 className="font-heading mt-5 text-4xl leading-[1.05] font-extrabold tracking-tight text-balance sm:text-5xl lg:text-[3.4rem]">
            {p.h1}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {p.sub}
          </p>

          {/* persona switcher = as 3 chamadas */}
          <div className="mt-7">
            <div className="mb-2.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Eu sou…
            </div>
            <div
              role="tablist"
              aria-label="Escolha seu perfil"
              className="inline-flex flex-wrap gap-1 rounded-4xl border border-border bg-muted/60 p-1.5"
            >
              {TABS.map((t) => {
                const active = audience === t.key
                const Icon = t.icon
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setAudience(t.key)}
                    className={cn(
                      "inline-flex h-10 items-center gap-2 rounded-4xl px-4 text-sm font-semibold transition-all",
                      active
                        ? "bg-background text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Icon className="size-4" />
                    {t.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="h-12 rounded-4xl px-6 text-[15px]">
              <Link href={p.ctaHref}>
                {p.ctaLabel}
                <ArrowRight className="size-4.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 rounded-4xl px-6 text-[15px]">
              <Link href={p.cta2Href}>{p.cta2Label}</Link>
            </Button>
          </div>

          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
            {[p.proof1, p.proof2].map((proof) => (
              <div key={proof} className="flex items-center gap-2 text-[13.5px] font-medium text-muted-foreground">
                <Check className="size-4 text-primary" />
                {proof}
              </div>
            ))}
          </div>
        </div>

        {/* right: morphing product window */}
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="overflow-hidden rounded-[20px] bg-card shadow-[0_24px_60px_-28px_rgba(22,22,26,.35)] ring-1 ring-foreground/10">
            <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-3">
              <span className="size-2.5 rounded-full bg-[#ff5f57]" />
              <span className="size-2.5 rounded-full bg-[#febc2e]" />
              <span className="size-2.5 rounded-full bg-[#28c840]" />
              <div className="ml-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground">
                <Lock className="size-3" />
                {p.winlabel}
              </div>
            </div>

            <div className="min-h-[392px] bg-primary/[0.035] p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    {p.kicker}
                  </div>
                  <div className="font-heading text-xl font-extrabold tracking-tight">{p.title}</div>
                </div>
                <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <GraduationCap className="size-5.5" />
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                {p.rows.map((r) => {
                  const Icon = r.icon
                  return (
                    <div
                      key={r.label}
                      className="flex items-center gap-3 rounded-[14px] bg-card p-3 ring-1 ring-foreground/5"
                    >
                      <div className={cn("flex size-10 flex-none items-center justify-center rounded-[11px]", toneChip[r.tone])}>
                        <Icon className="size-4.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">{r.label}</div>
                        <div className="text-[12.5px] text-muted-foreground">{r.meta}</div>
                      </div>
                      <div className={cn("rounded-[14px] px-2.5 py-1 text-[12.5px] font-bold", toneChip[r.tone])}>
                        {r.tag}
                      </div>
                    </div>
                  )
                })}
              </div>

              <Button className="mt-4 h-11 w-full rounded-4xl">{p.screenCta}</Button>
            </div>
          </div>
        </div>
      </div>

      {/* honest trust strip */}
      <div className="border-t border-border bg-background/60">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-6 py-5 lg:px-10">
          {PROOF_STRIP.map((c) => {
            const Icon = c.icon
            return (
              <div
                key={c.label}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3.5 py-2 text-[13.5px] font-medium text-foreground/80"
              >
                <Icon className="size-4 text-primary" />
                {c.label}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
